import { ENDPOINT } from '~/constants/endpoints';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type AddUnitMappingPayload,
  type AddUnitMappingResponse,
} from './types';
import { zodAddUnitMappingSchema } from './validations';

/**
 * Creates a unit mapping from an invalid UN/CEFACT unit to a valid UN/CEFACT unit.
 *
 * @param {Object} unitMappingData - The data for creating a new unit mapping.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Units/operation/add_unit_mapping_units_unit_mapping_post
 */
export const addUnitMapping = async (
  unitMappingData: AddUnitMappingPayload,
) => {
  try {
    const response = await vestigasApi
      .post(ENDPOINT.UNITS.ADD_MAPPING(), {
        json: unitMappingData,
        validate: { body: zodAddUnitMappingSchema },
      })
      .json<AddUnitMappingResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error adding unit mapping',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 409: {
          errorMessage = i18n.t(($) => $.unit.addUnitMapping.error409, {
            ns: 'data',
          });
          break;
        }

        case 422: {
          errorMessage = i18n.t(($) => $.unit.addUnitMapping.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.unit.addUnitMapping.error.generic, {
            ns: 'data',
          });
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
